"use client";

import { c as _c } from "react/compiler-runtime";
import Image from "next/image";
import { TerraLink } from "@/components/terra/component/TerraLink/TerraLink";
import { CommonDescription } from "@/components/terra/component/CommonDescription/CommonDescription";
import type { FilterableCardProps } from "@/components/terra/layouts/LayoutFilterableCards/FilterableCard";
import { useState, type MouseEvent } from "react";
import "./MarketplaceCard.css";

/** Same entry shape as {@link FilterableCardProps}, plus optional marketplace-only chrome. */
export type MarketplaceCardProps = Omit<FilterableCardProps, "imageDisplayMode"> & {
  /**
   * How the card image is shown: `"logo"` (contained in dashed area, default) or `"teaser"` (full-width cover).
   * @default "logo"
   */
  imageDisplayMode?: "logo" | "teaser";
  /** Optional 32px top banner: white text on Trimble blue (#0063A3). */
  textLabel?: string | null;
  /** Developer / company name shown below the card title. */
  developer?: string | null;
  /** Developer URL shown below the card title. */
  developerUrl?: string | null;
  /** Developer title shown below the card title. */
  developerTitle?: string | null;
  /** Category tags rendered above the CTA. */
  categoryTags?: string[];
  /**
   * Original AppDirect tag-group structure (e.g. Categories, Created By,
   * Featured, Geographies). Used by listing pages to drive a filter sidebar
   * — the card itself does not render this.
   */
  tagGroups?: Record<string, string[]>;
  titleId?: string;
  className?: string;
  isLCP?: boolean;
  displayType?: string;
};

/**
 * Marketplace-styled card using the same content fields as {@link FilterableCard}.
 */
export function MarketplaceCard(t0) {
  const $ = _c(54);
  const {
    image,
    imageDisplayMode: t1,
    header,
    description,
    button,
    truncate,
    truncateLength,
    textLabel,
    developer,
    developerTitle: t2,
    developerUrl,
    categoryTags,
    tagGroups,
    className,
    titleId,
    isLCP: t3
  } = t0;
  const imageDisplayMode = t1 === undefined ? "logo" : t1;
  const developerTitle = t2 === undefined ? "Developer" : t2;
  const isLCP = t3 === undefined ? false : t3;
  let t4;
  if ($[0] !== categoryTags || $[1] !== tagGroups) {
    t4 = tagGroups ? [...(tagGroups.Categories ?? []), ...(tagGroups.Certifications ?? [])] : categoryTags;
    $[0] = categoryTags;
    $[1] = tagGroups;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const pillTags = t4;
  const [isPointerOnCta, setPointerOnCta] = useState(false);
  const [isFocusOnCta, setFocusOnCta] = useState(false);
  const isCtaHighlighted = isPointerOnCta || isFocusOnCta;
  if (!header) {
    return null;
  }
  const destination = button?.destination;
  const hasDestination = Boolean(destination);
  const showCta = Boolean(button?.label && destination);
  let t5;
  if ($[3] !== header || $[4] !== titleId) {
    t5 = titleId ?? `marketplace-card-title-${hashStable(header)}`;
    $[3] = header;
    $[4] = titleId;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const headingId = t5;
  let t6;
  if ($[6] !== image || $[7] !== imageDisplayMode || $[8] !== isLCP) {
    t6 = () => {
      if (imageDisplayMode === "teaser") {
        if (!image?.url) {
          return <div className="w-full min-h-[200px] shrink-0 bg-gray-m1" aria-hidden="true" />;
        }
        return <div className="w-full min-h-[200px] shrink-0 bg-cover bg-center text-transparent select-none" style={{
          backgroundImage: `url(${image.url})`
        }} role="img" aria-label={image.alt || ""} />;
      }
      return <div className="px-8 py-6 pb-0 pt-16"><div className="relative w-full h-[75px] shrink-0 overflow-hidden bg-white">{image?.url ? <Image src={image.url} alt={image.alt ?? ""} fill={true} className="object-contain object-left" sizes="332px" loading={isLCP ? "eager" : "lazy"} /> : <div aria-hidden="true" className="h-full w-full" />}</div></div>;
    };
    $[6] = image;
    $[7] = imageDisplayMode;
    $[8] = isLCP;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const renderCardImage = t6;
  const t7 = destination ?? "#";
  const t8 = button?.label ? `${button.label}: ${header}` : header;
  const t9 = hasDestination ? undefined : -1;
  const t10 = hasDestination ? undefined : _temp as () => void;
  let t11;
  let t12;
  let t13;
  let t14;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => setPointerOnCta(true);
    t12 = () => setPointerOnCta(false);
    t13 = () => setFocusOnCta(true);
    t14 = () => setFocusOnCta(false);
    $[10] = t11;
    $[11] = t12;
    $[12] = t13;
    $[13] = t14;
  } else {
    t11 = $[10];
    t12 = $[11];
    t13 = $[12];
    t14 = $[13];
  }
  const t15 = `t2-marketplace-card group block h-full w-full text-gray-m9 max-w-[400px] rounded-[12px] no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-m9 focus-visible:ring-offset-2 focus-visible:ring-offset-white${hasDestination ? "" : " cursor-default"}`;
  const t16 = `t2-c relative bg-white box-border w-full h-full flex flex-col rounded-[12px] overflow-hidden border-2 t2-color-setting--light transition-colors duration-150 ${className ? ` ${className}` : ""} 
        ${isCtaHighlighted ? "border-blue-trmb" : "border-transparent"}`;
  let t17;
  if ($[14] !== textLabel) {
    t17 = textLabel && <span className={`absolute top-0 left-0 right-0 m-0 shrink-0 flex h-[32px] items-center px-8 text-med font-bold leading-none text-white ${textLabel ? "bg-blue-trmb" : "bg-transparent"} truncate uppercase justify-center`} title={textLabel ?? undefined}>{textLabel ?? undefined}</span>;
    $[14] = textLabel;
    $[15] = t17;
  } else {
    t17 = $[15];
  }
  let t18;
  if ($[16] !== renderCardImage) {
    t18 = renderCardImage();
    $[16] = renderCardImage;
    $[17] = t18;
  } else {
    t18 = $[17];
  }
  let t19;
  if ($[18] !== header || $[19] !== headingId) {
    t19 = <h3 id={headingId} className="text-gray-m9 text-[26px] font-light sm:leading-[1.2] break-words m-0 mb-2">{header}</h3>;
    $[18] = header;
    $[19] = headingId;
    $[20] = t19;
  } else {
    t19 = $[20];
  }
  let t20;
  if ($[21] !== developer || $[22] !== developerTitle || $[23] !== developerUrl) {
    t20 = developer ? developerUrl ? <p className="text-sm font-normal leading-snug m-0 mb-2">{developerTitle}: <TerraLink className="font-semibold" linkType="text" destination={developerUrl}>{developer}</TerraLink></p> : <p className="text-sm font-normal leading-snug m-0 mb-2">{developerTitle}: <span className="font-semibold">{developer}</span></p> : null;
    $[21] = developer;
    $[22] = developerTitle;
    $[23] = developerUrl;
    $[24] = t20;
  } else {
    t20 = $[24];
  }
  let t21;
  if ($[25] !== description || $[26] !== developer || $[27] !== truncate || $[28] !== truncateLength) {
    t21 = description ? <CommonDescription description={description} truncate={truncate} truncateLength={truncateLength} className={`text-base font-normal leading-relaxed line-clamp-3 m-0 [&_p]:m-0 ${developer ? "mt-0" : "mt-2"}`} /> : <div aria-hidden="true" className="min-h-[3.5rem]" />;
    $[25] = description;
    $[26] = developer;
    $[27] = truncate;
    $[28] = truncateLength;
    $[29] = t21;
  } else {
    t21 = $[29];
  }
  let t22;
  if ($[30] !== t19 || $[31] !== t20 || $[32] !== t21) {
    t22 = <div className="flex flex-col flex-grow px-8 py-6 max-sm:pt-4 min-h-0">{t19}{t20}{t21}</div>;
    $[30] = t19;
    $[31] = t20;
    $[32] = t21;
    $[33] = t22;
  } else {
    t22 = $[33];
  }
  let t23;
  if ($[34] !== pillTags) {
    t23 = pillTags && pillTags.length > 0 ? <div className="flex flex-wrap gap-2 px-8 pb-5 mt-auto">{pillTags.map(_temp2)}</div> : null;
    $[34] = pillTags;
    $[35] = t23;
  } else {
    t23 = $[35];
  }
  let t24;
  if ($[36] !== button || $[37] !== showCta) {
    t24 = showCta && button ? <div className="px-8 pb-12 pt-5 mt-auto shrink-0"><span className="t2-button t2-button--primary t2-button--medium rounded-full inline-block font-bold text-center uppercase [text-decoration:none]! bg-yellow-m10 border-2 border-yellow-m10 text-gray-m9 transition-[color,background-color,border-color,box-shadow] duration-150 ease-in-out group-hover:bg-yellow-m10-hover group-hover:border-yellow-m10-hover text-lg tracking-[1.44px] leading-7 py-4 px-9 max-sm:text-base max-sm:py-1.5 max-sm:px-[34px]">{button.label}</span></div> : null;
    $[36] = button;
    $[37] = showCta;
    $[38] = t24;
  } else {
    t24 = $[38];
  }
  let t25;
  if ($[39] !== headingId || $[40] !== t16 || $[41] !== t17 || $[42] !== t18 || $[43] !== t22 || $[44] !== t23 || $[45] !== t24) {
    t25 = <article className={t16} aria-labelledby={headingId}>{t17}{t18}{t22}{t23}{t24}</article>;
    $[39] = headingId;
    $[40] = t16;
    $[41] = t17;
    $[42] = t18;
    $[43] = t22;
    $[44] = t23;
    $[45] = t24;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  let t26;
  if ($[47] !== t10 || $[48] !== t15 || $[49] !== t25 || $[50] !== t7 || $[51] !== t8 || $[52] !== t9) {
    t26 = <TerraLink disableStyles={true} destination={t7} ariaLabel={t8} tabIndex={t9} onClickHandler={t10} onMouseEnter={t11} onMouseLeave={t12} onFocus={t13} onBlur={t14} className={t15}>{t25}</TerraLink>;
    $[47] = t10;
    $[48] = t15;
    $[49] = t25;
    $[50] = t7;
    $[51] = t8;
    $[52] = t9;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  return t26;
}

/** Tiny deterministic id suffix so multiple cards on a page get unique heading ids. */
function _temp2(tag, index) {
  return <span key={`${tag}-${index}`} className="inline-block bg-gray-m1 px-3 py-1 text-sm font-bold leading-tight">{tag}</span>;
}
function _temp(e) {
  e.preventDefault();
}
function hashStable(input: string): string {
  let h = 0;
  for (let i = 0; i < input.length; i += 1) {
    h = (h << 5) - h + input.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h).toString(36);
}